import { useState, useEffect } from 'react';
import { m } from 'framer-motion';

import { cn } from 'src/lib/utils';

// ─────────────────────────────────────────────────────────────────────────────
//  SplashScreen — Cinematic brand reveal for Athory
//
//  A sequenced, one-shot splash shown for full-screen boot/auth/callback flows.
//  Phases: Atmosphere → Logo reveal → Brand name → Tagline → Progress
//
//  All colors use semantic CSS variables (--foreground, --background, --primary,
//  --secondary) so the splash adapts to the active theme and transitions
//  seamlessly into the application.
// ─────────────────────────────────────────────────────────────────────────────

// ── Timing choreography (seconds) ────────────────────────────────────────
const T = {
  logoIn: 0.3,
  brandIn: 0.8,
  separatorIn: 1.15,
  taglineIn: 1.2,
  progressIn: 1.5,
  progressFill: 2.0,
};

// ── Spring presets ───────────────────────────────────────────────────────
const SPRING_REVEAL = { type: 'spring' as const, stiffness: 80, damping: 20, mass: 1 };
const EASE_OUT_EXPO: [number, number, number, number] = [0.16, 1, 0.3, 1];

// ── Sub-components ───────────────────────────────────────────────────────

/** Staggered letter-by-letter reveal with blur-up effect */
function LetterReveal({
  text,
  delay,
  className,
  letterSpacing = '0.22em',
}: {
  text: string;
  delay: number;
  className?: string;
  letterSpacing?: string;
}) {
  return (
    <span className={cn('inline-flex', className)} style={{ letterSpacing }}>
      {text.split('').map((char, i) => (
        <m.span
          key={i}
          className="inline-block"
          initial={{ opacity: 0, y: 14, filter: 'blur(6px)' }}
          animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
          transition={{
            duration: 0.5,
            delay: delay + i * 0.04,
            ease: EASE_OUT_EXPO,
          }}
        >
          {char === ' ' ? '\u00A0' : char}
        </m.span>
      ))}
    </span>
  );
}

/** Converging twin-pulse progress indicator with center bloom */
function SplashProgress({ delay, fillDuration }: { delay: number; fillDuration: number }) {
  const cycle = fillDuration * 1.2;

  return (
    <m.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay }}>
      <div
        className="relative overflow-hidden rounded-full"
        style={{
          width: 200,
          height: 2,
          background: 'hsl(var(--foreground) / 0.06)',
        }}
      >
        {/* Ambient track glow — breathes in sync with convergence */}
        <m.div
          className="absolute inset-0 rounded-full"
          style={{ background: 'hsl(var(--primary) / 0.1)' }}
          animate={{ opacity: [0.2, 0.6, 0.2] }}
          transition={{
            duration: cycle,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: delay + 0.1,
          }}
        />

        {/* Left-to-center sweep */}
        <m.div
          className="absolute inset-y-0 rounded-full"
          style={{
            width: '40%',
            background:
              'linear-gradient(90deg, transparent, hsl(var(--primary) / 0.5), transparent)',
          }}
          animate={{ left: ['-20%', '30%', '-20%'] }}
          transition={{
            duration: cycle,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: delay + 0.1,
          }}
        />

        {/* Right-to-center sweep */}
        <m.div
          className="absolute inset-y-0 rounded-full"
          style={{
            width: '40%',
            background:
              'linear-gradient(90deg, transparent, hsl(var(--primary) / 0.5), transparent)',
          }}
          animate={{ left: ['80%', '30%', '80%'] }}
          transition={{
            duration: cycle,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: delay + 0.1,
          }}
        />

        {/* Center bloom — peaks when both sweeps converge */}
        <m.div
          className="absolute inset-y-0 left-1/2 -translate-x-1/2 rounded-full"
          style={{
            width: 6,
            background: 'hsl(var(--primary) / 0.8)',
            boxShadow: '0 0 10px 2px hsl(var(--primary) / 0.25)',
          }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{
            duration: cycle,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: delay + 0.1,
          }}
        />
      </div>
    </m.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────────────────────────────────────

interface SplashScreenProps {
  className?: string;
}

export default function SplashScreen({ className }: SplashScreenProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[9998] flex items-center justify-center overflow-hidden bg-background select-none',
        className
      )}
    >
      {/* ── Background atmosphere ─────────────────────────────────── */}

      {/* Radial gradient — subtle brand warmth at center */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 50% 45% at 50% 48%, hsl(var(--secondary) / 0.08) 0%, transparent 100%)',
        }}
      />

      {/* Noise grain texture — tactile depth */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          opacity: 0.04,
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E\")",
          backgroundSize: '128px 128px',
        }}
      />

      {/* ── Center content ────────────────────────────────────────── */}

      <div className="relative flex flex-col items-center gap-4">
        {/* Logo with backlight glow */}
        <m.div
          className="relative flex h-28 w-28 items-center justify-center"
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ ...SPRING_REVEAL, delay: T.logoIn }}
        >
          {/* Soft radial backlight — breathes continuously after entrance */}
          <m.div
            className="pointer-events-none absolute rounded-full"
            style={{
              width: 180,
              height: 180,
              background:
                'radial-gradient(circle, hsl(var(--secondary) / 0.12) 0%, transparent 70%)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.6, 1, 0.6] }}
            transition={{
              duration: 3,
              ease: 'easeInOut',
              repeat: Infinity,
              delay: T.logoIn + 0.3,
            }}
          />

          {/* Athory logo — rendered as-is, no filters */}
          <m.img
            src="/assets/illustrations/Athory.svg"
            alt="Athory"
            className="relative z-10 h-16 w-16 object-contain"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: T.logoIn + 0.2 }}
          />
        </m.div>

        {/* Brand name + tagline */}
        <div className="flex flex-col items-center gap-3.5">
          <LetterReveal
            text="ATHORY"
            delay={T.brandIn}
            letterSpacing="0.28em"
            className="text-sm font-medium text-foreground"
          />

          {/* Thin separator line */}
          <m.div
            style={{
              width: 28,
              height: 1,
              background: 'hsl(var(--foreground) / 0.15)',
            }}
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            transition={{ duration: 0.6, delay: T.separatorIn, ease: EASE_OUT_EXPO }}
          />

          {/* Tagline */}
          <m.p
            className="text-[11px] tracking-[0.14em] text-muted-foreground"
            style={{ opacity: 0 }}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 0.6, y: 0 }}
            transition={{ duration: 0.7, delay: T.taglineIn, ease: EASE_OUT_EXPO }}
          >
            Inventory Intelligence
          </m.p>
        </div>

        {/* Progress bar */}
        <SplashProgress delay={T.progressIn} fillDuration={T.progressFill} />
      </div>
    </div>
  );
}
